/**
 * Voice Visualizer
 * 
 * Beautiful audio visualization for the Agent Mode
 * Shows pulsing animation when listening/speaking
 */

import React, { useEffect, useRef } from 'react';

interface VoiceVisualizerProps {
  isActive: boolean;
  audioLevel: number;
  status: 'idle' | 'listening' | 'thinking' | 'speaking';
}

export const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({
  isActive,
  audioLevel,
  status
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;

    let phase = 0;

    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      if (!isActive) {
        // Draw static circle when idle
        ctx.beginPath();
        ctx.arc(centerX, centerY, 80, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(147, 51, 234, 0.3)'; // purple-600
        ctx.lineWidth = 2;
        ctx.stroke();
        return;
      }

      // Animated visualization when active
      const barCount = 64;
      const baseRadius = 60;
      const maxAmplitude = 40;

      for (let i = 0; i < barCount; i++) {
        const angle = (i / barCount) * Math.PI * 2;
        
        // Create wave effect
        const wave = Math.sin(angle * 3 + phase) * 0.5 + 0.5;
        const amplitude = (audioLevel * 0.7 + wave * 0.3) * maxAmplitude;
        
        const x1 = centerX + Math.cos(angle) * baseRadius;
        const y1 = centerY + Math.sin(angle) * baseRadius;
        const x2 = centerX + Math.cos(angle) * (baseRadius + amplitude);
        const y2 = centerY + Math.sin(angle) * (baseRadius + amplitude);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);

        // Color based on status
        let gradient;
        if (status === 'listening') {
          gradient = ctx.createLinearGradient(x1, y1, x2, y2);
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.3)'); // green
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0.8)');
        } else if (status === 'thinking') {
          gradient = ctx.createLinearGradient(x1, y1, x2, y2);
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.3)'); // blue
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0.8)');
        } else if (status === 'speaking') {
          gradient = ctx.createLinearGradient(x1, y1, x2, y2);
          gradient.addColorStop(0, 'rgba(147, 51, 234, 0.3)'); // purple
          gradient.addColorStop(1, 'rgba(147, 51, 234, 0.8)');
        } else {
          gradient = ctx.createLinearGradient(x1, y1, x2, y2);
          gradient.addColorStop(0, 'rgba(147, 51, 234, 0.3)');
          gradient.addColorStop(1, 'rgba(147, 51, 234, 0.8)');
        }

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      // Draw center glow
      const glowRadius = baseRadius * (0.5 + audioLevel * 0.5);
      const glowGradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, glowRadius
      );
      
      if (status === 'listening') {
        glowGradient.addColorStop(0, 'rgba(34, 197, 94, 0.4)');
        glowGradient.addColorStop(1, 'rgba(34, 197, 94, 0)');
      } else if (status === 'thinking') {
        glowGradient.addColorStop(0, 'rgba(59, 130, 246, 0.4)');
        glowGradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
      } else {
        glowGradient.addColorStop(0, 'rgba(147, 51, 234, 0.4)');
        glowGradient.addColorStop(1, 'rgba(147, 51, 234, 0)');
      }

      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, glowRadius, 0, Math.PI * 2);
      ctx.fill();

      phase += 0.05;
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, audioLevel, status]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        width={400}
        height={400}
        className="mx-auto"
        style={{ filter: 'blur(0.5px)' }}
      />
      
      {/* Pulsing outer ring */}
      {isActive && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`
            w-64 h-64 rounded-full animate-ping
            ${status === 'listening' ? 'bg-success/20' :
              status === 'thinking' ? 'bg-info/20' :
              status === 'speaking' ? 'bg-accent/20' :
              'bg-accent/20'}
          `} style={{ animationDuration: '2s' }} />
        </div>
      )}
    </div>
  );
};

